import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { ownedError } from 'core/utils/owned-error';
import { Subject, Subscription } from 'rxjs';
import { Ordering } from 'shared/criteria/Criteria';
import { ListData, ListEagerProviderDirective, ListPaginatedProviderDirective } from 'shared/modules/list/providers';
import { FilterSource } from 'shared/modules/table/table-utils/table-directives';

@Directive({
  selector: 'app-list-container',
  standalone: false,
})
export class ListContainerDirective {
}

@Directive({
  selector: 'app-list-head',
  standalone: false,
})
export class ListHeadDirective {
}

@Directive({
  selector: 'app-list-body',
  standalone: false,
})
export class ListBodyDirective {
}

@Directive({
  selector: 'app-list-actions',
  standalone: false,
})
export class ListActionsDirective {
}

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ListComponent<T> implements AfterContentInit, OnDestroy {
  @ContentChild(ListEagerProviderDirective)
  eagerProvider?: ListEagerProviderDirective<T>;

  @ContentChild(ListPaginatedProviderDirective)
  paginatedProvider?: ListPaginatedProviderDirective<T>;

  private _pageChanged = new EventEmitter<PageEvent>();
  private _loading = false;
  private _data = new Array<T>();
  virtualRows: T[][] = [];
  loadError = false;
  private _reloader = new Subject();
  private _filter = '';
  private _ordering?: Ordering;
  private _noDataText?: string;
  private _virtualScroll = false;
  private _subscription = new Subscription();

  get loading(): boolean {
    return this._loading;
  }

  constructor(private cd: ChangeDetectorRef, private translateService: TranslateService) {}

  get reloader(): Subject<any> {
    return this._reloader;
  }

  @Input()
  set reloader(value: Subject<any>) {
    this._reloader = value;
  }

  get data(): Array<T> {
    return this._data;
  }

  @ViewChild(FilterSource)
  private _filterSource!: FilterSource;

  get filterSource(): FilterSource {
    return this._filterSource;
  }

  @Input()
  set filterSource(value: FilterSource) {
    this._filterSource = value;
  }

  get filter(): string {
    return this._filter;
  }

  set filter(filter: string) {
    this._filter = filter;
  }

  get ordering(): Ordering | undefined {
    return this._ordering;
  }

  @Input()
  set ordering(ordering: Ordering | undefined) {
    this._ordering = ordering;
  }

  get noDataText(): string {
    return this._noDataText ?? this.translateService.instant('COMMON.no_data');
  }

  @Input()
  set noDataText(value: string) {
    this._noDataText = value;
  }

  get virtualScroll(): boolean {
    return this._virtualScroll;
  }

  @Input()
  set virtualScroll(value: boolean) {
    this._virtualScroll = value;
  }

  @Output()
  get pageChanged(): EventEmitter<PageEvent> {
    return this._pageChanged;
  }

  ngAfterContentInit(): void {
    this._subscription.add(this.paginatedProvider?.pageChanged.subscribe((e: PageEvent) => this.pageChanged.emit(e)));

    const loader = this.eagerProvider || this.paginatedProvider;

    if (!loader) {
      throw ownedError(new Error('Loader is not specified'));
    }

    this._subscription.add(loader.loading.subscribe(() => {
      this._loading = true;
      this.loadError = false;
      this.cd.markForCheck();
    }));

    this._subscription.add(loader.failed.subscribe(() => {
      this.loadError = true;
      this.cd.markForCheck();
    }));

    this._subscription.add(loader.finished.subscribe(() => {
      this._loading = false;
      this.cd.markForCheck();
    }));

    this._subscription.add(loader.loaded
      .subscribe((data: ListData<T>) => {
        this._data = data.items;
        this.virtualRows = [];
        for (let index = 0; index < data.items.length; index += 2) {
          this.virtualRows.push(data.items.slice(index, index + 2));
        }
        this._loading = false;
        this.cd.markForCheck();
      }));

    if (this.filterSource) {
      this._subscription.add(this.filterSource.filterChanged
        .subscribe((filter: string) => {
          this.filter = filter;

          this.reloader.next(undefined);
        }));
    }

    if (loader instanceof ListEagerProviderDirective) {
      this._subscription.add(this.reloader
        .subscribe(() => loader.loadAll(this.filter, this.ordering)));
    }

    if (loader instanceof ListPaginatedProviderDirective) {
      this._subscription.add(this.reloader
        .subscribe(() => loader.loadPage(null, this.filter, this.ordering)));
    }

    !this.filterSource && this.reloader.next(undefined);
  }

  ngOnDestroy(): void {
    this.eagerProvider?.cancel();
    this.paginatedProvider?.cancel();
    this._subscription.unsubscribe();
  }

  trackByIndex(i: number): number {
    return i;
  }
}
